import React, { useMemo, useState } from "react";
import { Delivery, Inning, MatchState } from "../matchTypes";
import { currentInning, getBowlerStats, getCurrentRunRate, getMatchResult, getPartnership, getRequiredRunRate } from "../engine/MatchEngine";
import { assignedOfficials } from "../utils/matchOfficials";
import { formatPlayerName } from "../utils/nameFormatter";
import TeamLogo from "../components/broadcast/TeamLogo";
import ShareInningsCard from "../components/ShareInningsCard";
import { shareOrDownloadInningsCard } from "../utils/renderInningsCard";
import Watermark from "../components/Watermark";
import { generateDeliveryCommentary } from "../utils/commentaryGenerator";
import "../styles/summary.css";

function overs(balls: number, ballsPerOver: number): string {
  return `${Math.floor(balls / ballsPerOver)}.${balls % ballsPerOver}`;
}

function currentOver(inning: Inning, ballsPerOver: number): Delivery[] {
  const legalBalls = inning.deliveries.filter(delivery => delivery.isLegal).length;
  if (!legalBalls) return inning.deliveries;
  const overStart = legalBalls - (legalBalls % ballsPerOver || ballsPerOver);
  let seen = 0;
  let first = 0;
  for (let index = 0; index < inning.deliveries.length; index += 1) {
    if (inning.deliveries[index].isLegal) {
      seen += 1;
      if (seen === overStart + 1) {
        first = index;
        break;
      }
    }
  }
  while (first > 0 && !inning.deliveries[first - 1].isLegal) first -= 1;
  return inning.deliveries.slice(first);
}

function chipClass(delivery: Delivery): string {
  const base = "flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-xs font-black";
  if (delivery.isWicket && delivery.isLegal) return `${base} bg-red-500 text-white`;
  if (delivery.extraType === "wide") return `${base} bg-sky-600 text-white`;
  if (delivery.runs === 4) return `${base} bg-emerald-500 text-white`;
  if (delivery.runs === 6) return `${base} bg-blue-500 text-white`;
  if (delivery.extraType) return `${base} bg-slate-600 text-white`;
  return `${base} bg-slate-200 text-slate-900`;
}

function Icon({ children }: { children: React.ReactNode }) {
  return <span className="summary-icon" aria-hidden="true">{children}</span>;
}

export default function MatchSummary({ state, isPro = false, matchId }: { state: MatchState; isPro?: boolean; matchId?: string }) {
  const teamName = (team: "A" | "B") => team === "A" ? state.teamAName : state.teamBName;
  const teamLogo = (team: "A" | "B") => team === "A" ? state.teamALogo : state.teamBLogo;
  const ballsPerOver = state.ballsPerOver || 6;
  const status = state.status || "draft";
  const isCompleted = status === "completed" || status === "abandoned";
  const isLive = status === "live";
  const activeEngineInnings = currentInning(state);
  const startedInnings = useMemo(() => state.innings.filter(inning => inning.totalBalls > 0 || inning.totalRuns > 0 || inning.deliveries.length > 0), [state.innings]);
  const firstInnings = startedInnings[0];
  const secondInnings = startedInnings[1];
  const activeInnings = (isCompleted ? startedInnings[startedInnings.length - 1] : activeEngineInnings) || firstInnings;
  const phase: "first" | "break" | "second" | "completed" | "empty" = useMemo(() => {
    if (isCompleted) return "completed";
    if (!startedInnings.length) return "empty";
    if (state.format === "INDOOR") return "first";
    if (state.currentInnings >= 1) return "second";
    if (startedInnings[0]?.completed && state.currentInnings === 0) return "break";
    return "first";
  }, [isCompleted, startedInnings, state.format, state.currentInnings]);
  const [showCard, setShowCard] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const safeActive = activeInnings;
  const result = getMatchResult(state);
  const battingPlayers = safeActive?.battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
  const bowlingPlayers = safeActive?.battingTeam === "A" ? state.teamBPlayers : state.teamAPlayers;
  const striker = battingPlayers.find(player => player.id === state.strikerId);
  const nonStriker = battingPlayers.find(player => player.id === state.nonStrikerId);
  const bowler = bowlingPlayers.find(player => player.id === state.bowlerId);
  const partnership = getPartnership(state, ballsPerOver);
  const deliveries = safeActive ? currentOver(safeActive, ballsPerOver) : [];
  const crr = safeActive ? getCurrentRunRate(safeActive, ballsPerOver) : 0;
  const rrr = secondInnings ? getRequiredRunRate(secondInnings, ballsPerOver) : null;
  const target = secondInnings?.target ?? null;
  const runsNeeded = target === null || !secondInnings ? null : Math.max(0, target - secondInnings.totalRuns);
  const ballsRemaining = secondInnings?.oversLimit ? Math.max(0, secondInnings.oversLimit * ballsPerOver - secondInnings.totalBalls) : null;
  const officials = assignedOfficials(state.officials);
  const dateTime = state.matchDate ? `${new Date(state.matchDate).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}${state.matchTime ? ` ${state.matchTime}` : ""}` : null;
  const toss = state.toss?.winnerTeam && state.toss.decision ? `${teamName(state.toss.winnerTeam)} elected to ${state.toss.decision === "BAT" ? "bat" : "bowl"}` : null;
  const scoreFor = (team: "A" | "B") => {
    const innings = startedInnings.find(item => item.battingTeam === team);
    return innings ? `${innings.totalRuns}/${innings.wickets}` : "—";
  };
  const oversFor = (team: "A" | "B") => {
    const innings = startedInnings.find(item => item.battingTeam === team);
    return innings ? overs(innings.totalBalls, ballsPerOver) : "0.0";
  };
  const latestWicket = useMemo(() => {
    if (!safeActive) return null;
    for (let index = safeActive.deliveries.length - 1; index >= 0; index -= 1) {
      const delivery = safeActive.deliveries[index];
      if (delivery.isWicket) {
        const player = battingPlayers.find(item => item.id === delivery.batterId);
        return { name: player ? formatPlayerName(player.name) : "Batter", over: overs(safeActive.deliveries.slice(0, index + 1).filter(item => item.isLegal).length, ballsPerOver), label: delivery.dismissal || "Wicket" };
      }
    }
    return null;
  }, [battingPlayers, ballsPerOver, safeActive]);
  const lastBoundary = useMemo(() => {
    if (!safeActive) return null;
    for (let index = safeActive.deliveries.length - 1; index >= 0; index -= 1) {
      const delivery = safeActive.deliveries[index];
      if (delivery.runs === 4 || delivery.runs === 6) {
        const player = battingPlayers.find(item => item.id === delivery.batterId);
        return { name: player ? formatPlayerName(player.name) : "Batter", runs: delivery.runs, over: overs(safeActive.deliveries.slice(0, index + 1).filter(item => item.isLegal).length, ballsPerOver) };
      }
    }
    return null;
  }, [battingPlayers, ballsPerOver, safeActive]);

  async function shareCard(): Promise<void> {
    if (!startedInnings.length) return;
    setGenerating(true);
    const innings = startedInnings[0];
    const result = await shareOrDownloadInningsCard("innings-card-preview", teamName(innings.battingTeam));
    setMessage(result.success ? (result.method === "share" ? "Shared successfully" : "Downloaded match card") : "Unable to create the match card");
    setGenerating(false);
    window.setTimeout(() => setMessage(null), 4000);
  }

  function shareLiveLink(): void {
    if (!matchId) {
      setMessage("Match link is not available yet");
      return;
    }
    const link = `${window.location.origin}/?match=${matchId}`;
    const text = `${state.teamAName} vs ${state.teamBName}\nWatch the live score on WatchWicket\n${link}`;
    const showCopied = () => { setCopied(true); setMessage("Live score link copied"); window.setTimeout(() => { setCopied(false); setMessage(null); }, 4000); };
    const showFailed = () => { setMessage("Unable to copy — long-press the link to share"); window.setTimeout(() => setMessage(null), 4000); };
    const tryClipboard = () => {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        navigator.clipboard.writeText(link).then(showCopied).catch(showFailed);
      } else {
        showFailed();
      }
    };
    if (navigator.share) {
      navigator.share({ title: "WatchWicket Live Match", text })
        .then(() => setMessage("Shared successfully"))
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          tryClipboard();
        });
    } else {
      tryClipboard();
    }
  }

  if (!safeActive) return (
    <div className="summary-empty">
      <Watermark />
      <p>No innings data available yet.</p>
    </div>
  );

  const situationText = runsNeeded !== null && ballsRemaining !== null && runsNeeded > 0
    ? `Need ${runsNeeded} runs from ${ballsRemaining} balls`
    : runsNeeded === 0 ? "Target reached" : "Innings in progress";
  const formatLabel = state.oversLimit ? `${state.format} • ${state.oversLimit} overs` : state.format;
  const bowlerStats = bowler ? getBowlerStats(safeActive, bowler.id, ballsPerOver) : null;
  const officialsText = officials.length > 0
    ? officials.map(item => item.official.name).join(", ")
    : "TBC";
  const inningsLabel = phase === "completed" ? "MATCH RESULT"
    : phase === "break" ? "INNINGS BREAK"
    : state.currentInnings === 0 ? "1ST INNINGS"
    : state.currentInnings === 1 ? "2ND INNINGS"
    : `${state.currentInnings + 1}TH INNINGS`;
  const targetDisplay = target ?? (firstInnings ? firstInnings.totalRuns + 1 : "—");

  return (
    <div className="summary-page">
      <Watermark />
      <div className="summary-content">

        {/* ═══ SECTION 1: UNIFIED STADIUM MATCH HERO ═══ */}
        <section className="summary-hero">
          <div className="hero-glow" />
          <div className="hero-status">
            <span>{formatLabel}</span>
            <b className={isLive ? "live" : "complete"}>
              {isLive ? "LIVE" : isCompleted ? "COMPLETED" : "DRAFT"}
            </b>
          </div>
          <div className="innings-pill">{inningsLabel}</div>
          <div className="hero-teams">
            <div className="hero-team">
              <TeamLogo logoUrl={teamLogo("A")} teamName={state.teamAName} size="lg" />
              <strong>{state.teamAName}</strong>
              <div className="hero-score">{scoreFor("A")}</div>
              <small>({oversFor("A")} overs)</small>
            </div>
            <div className="hero-vs">VS</div>
            <div className="hero-team">
              <TeamLogo logoUrl={teamLogo("B")} teamName={state.teamBName} size="lg" />
              <strong>{state.teamBName}</strong>
              <div className="hero-score">{scoreFor("B")}</div>
              <small>({oversFor("B")} overs)</small>
            </div>
          </div>
          <div className="chase-panel">
            <div className="chase-main">
              <span>TARGET {targetDisplay}</span>
              <strong>{situationText}</strong>
            </div>
            <div className="metric">
              <small>RRR</small>
              <strong>{rrr === null ? "—" : rrr.toFixed(2)}</strong>
            </div>
            <div className="metric">
              <small>CRR</small>
              <strong>{crr.toFixed(2)}</strong>
            </div>
          </div>
          {isCompleted && result && <div className="hero-result">{result}</div>}
        </section>

        {/* ═══ SECTION 2: SNAPSHOT ROW ═══ */}
        <section className="snapshot-grid">
          <div className="summary-panel snapshot-card">
            <h3><Icon>▰</Icon>At Crease</h3>
            <div className="player-line">
              <i className="green-dot" />
              {striker ? formatPlayerName(striker.name) : "—"}
              <b>{striker ? `${striker.runs} (${striker.balls})` : ""}</b>
            </div>
            <div className="player-line">
              {nonStriker ? formatPlayerName(nonStriker.name) : "—"}
              <b>{nonStriker ? `${nonStriker.runs} (${nonStriker.balls})` : ""}</b>
            </div>
          </div>
          <div className="summary-panel snapshot-card">
            <h3><Icon>●</Icon>Current Bowler</h3>
            {bowler && bowlerStats ? (
              <>
                <div className="player-name">{formatPlayerName(bowler.name)}</div>
                <div className="muted">{bowlerStats.overs} ov • {bowlerStats.runs} runs • {bowlerStats.wickets}W</div>
                <div className="muted">Econ {bowlerStats.economy}</div>
              </>
            ) : (
              <div className="muted">No bowler</div>
            )}
          </div>
          <div className="summary-panel snapshot-card">
            <h3><Icon>☷</Icon>This Over</h3>
            <div className="delivery-row">
              {deliveries.length > 0
                ? deliveries.map((delivery, index) => (
                    <span key={index} className={chipClass(delivery)}>{delivery.display}</span>
                  ))
                : <span className="muted">No deliveries</span>}
            </div>
          </div>
        </section>

        {/* ═══ SECTION 3: KEY MATCH MOMENTS ═══ */}
        {(latestWicket || lastBoundary || partnership.runs > 0) && (
          <section className="summary-panel moments">
            <div className="section-heading">
              <h2><Icon>★</Icon>Key Match Moments</h2>
              <button type="button">View all</button>
            </div>
            <div className="moment-grid">
              {latestWicket && (
                <div className="moment-card">
                  <Icon>▥</Icon>
                  <div>
                    <small>Latest Wicket</small>
                    <strong>{latestWicket.name}</strong>
                    <span>{latestWicket.label} • {latestWicket.over}</span>
                  </div>
                </div>
              )}
              {lastBoundary && (
                <div className="moment-card">
                  <Icon>◉</Icon>
                  <div>
                    <small>Last Boundary</small>
                    <strong>{lastBoundary.name}</strong>
                    <span>{lastBoundary.runs === 4 ? "FOUR" : "SIX"} • {lastBoundary.over}</span>
                  </div>
                </div>
              )}
              {partnership.runs > 0 && (
                <div className="moment-card">
                  <Icon>⇄</Icon>
                  <div>
                    <small>Current Partnership</small>
                    <strong>{partnership.runs} runs</strong>
                    <span>({overs(partnership.balls, ballsPerOver)} overs)</span>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ═══ SECTION 4: MATCH DETAILS ═══ */}
        <section className="summary-panel details">
          <h2><Icon>▤</Icon>Match Details</h2>
          <div className="details-row">
            {dateTime && (
              <div>
                <Icon>▦</Icon>
                <small>Date &amp; Time</small>
                <strong>{dateTime}</strong>
              </div>
            )}
            {state.matchLocation && (
              <div>
                <Icon>●</Icon>
                <small>Venue</small>
                <strong>{state.matchLocation}</strong>
              </div>
            )}
            <div>
              <Icon>⚔</Icon>
              <small>Format</small>
              <strong>{formatLabel}</strong>
            </div>
            {toss && (
              <div>
                <Icon>◉</Icon>
                <small>Toss</small>
                <strong>{toss}</strong>
              </div>
            )}
            <div>
              <Icon>♟</Icon>
              <small>Officials</small>
              <strong>{officialsText}</strong>
            </div>
          </div>
        </section>

        {/* ═══ SECTION 5: BALL-BY-BALL COMMENTARY ═══ */}
        {state.commentary.length > 0 && (
          <section className="summary-panel">
            <div className="section-heading">
              <h2><Icon>▤</Icon>Ball-by-Ball Commentary</h2>
            </div>
            <div className="space-y-2" style={{ maxHeight: "28rem", overflowY: "auto" }}>
              {[...state.commentary].reverse().map((comment, idx) => (
                <div key={idx} className="commentary-line" style={{ padding: "0.5rem 0.75rem", borderRadius: "0.5rem", background: "rgba(255,255,255,0.03)" }}>
                  <span className="muted tabular-nums" style={{ fontWeight: 600, fontSize: "0.8rem" }}>
                    {comment.over}.{comment.ball}
                  </span>
                  <span className="muted" style={{ margin: "0 0.4rem" }}>—</span>
                  <span style={{ fontSize: "0.85rem" }}>{comment.text}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ═══ SECTION 6: SHARE MATCH ═══ */}
        <section className="summary-panel share-strip">
          <div className="share-copy">
            <Icon>●</Icon>
            <div>
              <h2>Share Match</h2>
              <p>Share live match link or generate a scorecard</p>
            </div>
          </div>
          <div className="share-actions">
            <button type="button" onClick={shareLiveLink} disabled={!matchId} className="share-live">
              ↗ <span>{copied ? "Copied" : "Share Live Link"}</span>
            </button>
            <button type="button" onClick={() => setShowCard(value => !value)} disabled={generating} className="share-card">
              ▣ <span>{showCard ? "Hide Card" : "Match Card"}</span>
            </button>
          </div>
          {message && <div className="share-message">{message}</div>}
        </section>

        {showCard && (
          <div id="innings-card-preview" className="share-card-preview">
            <button type="button" onClick={shareCard} disabled={generating}>
              {generating ? "Generating..." : "Generate & Share Card"}
            </button>
            <ShareInningsCard state={state} inningsIndex={state.innings.indexOf(startedInnings[0])} />
          </div>
        )}
      </div>
    </div>
  );
}
